import useSWR from "swr";
import { message } from "antd";
import {
  ApplicationStatus,
  ResponseStep,
  OnboardingApplication,
  // The IDV vocabulary lives in shared: this file's own copy silently omitted
  // `expired` and `declined`, which the server sends.
  PersonIdSessionStatus,
  PersonIdSessionDecision,
} from "@dreambigger/shared/src/types";
import {
  api,
  callApiWithRetry,
  captureApiError,
} from "@dreambigger/shared/src/api/acquire";
import { useAuthToken } from "../passwordless-auth";
import { useFetcher } from "./";
import axios from "axios";
import { useRouter } from "next/router";
import * as Sentry from "@sentry/nextjs";
import {
  getAdminApplication,
  isInAdminMode,
} from "../components/AdminApplicationWrapper";
import { browserTimeZone } from "@dreambigger/shared/src/utils";
import { browserId, BROWSER_ID_HEADER } from "./browser-id";

// All the paths for this api domain.
const basePath = "/onboarding-applications";
const paths = {
  applicationById: (id: string) => basePath + `/${id}`,
  myApplications: () => basePath + `/me`,
  signingUrlById: (id: string) => basePath + `/${id}/docusign-signing-url`,
  getAtomicSessionUrl: (id: string) => basePath + `/${id}/atomic-session-url`,
  getNextStep: (id: string) => basePath + `/${id}/next-step`,
  pullCorelationCredit: (id: string) =>
    basePath + `/${id}/financial-application-credit-reports`,
  runFisGkycEvaluations: (id: string) =>
    basePath + `/${id}/fis-gkyc-evaluations`,
  runSocureIdvSession: (id: string) => basePath + `/${id}/socure-idv-sessions`,
  getSocureIdvSession: (id: string, sessionUuid: string) =>
    basePath + `/${id}/socure-idv-sessions/${sessionUuid}`,
  runExperianQueries: (id: string) => basePath + `/${id}/experian`,
  getFinancialApplicationCreditReport: (id: string) =>
    `/financial-application-credit-reports/${id}`,
};

const useApplication = (financialInstitutionId: string) => {
  const router = useRouter();
  const tokenHelper = useAuthToken(financialInstitutionId);
  const { fetchWithToken } = useFetcher(api, financialInstitutionId);

  // If the application is in admin mode, obtain the initial admin application from the store (AdminApplicationWrapper, which uses context from AdminApplicationProvider)
  const isAdminMode = isInAdminMode();
  const initialAdminApp = getAdminApplication();

  // Dynamic SWR key: switches between single app (admin) vs user's apps (regular)
  // - Admin mode: fetches one application by ID (returns OnboardingApplication)
  // - Regular mode: fetches user's applications (returns { records: OnboardingApplication[] })
  const dynamicSWRKey: [string, string] | null = tokenHelper.jwtIsExpired()
    ? null
    : isAdminMode && initialAdminApp
    ? [paths.applicationById(initialAdminApp.id), tokenHelper.getJwtToken()]
    : [paths.myApplications(), tokenHelper.getJwtToken()];

  const {
    data: rawSWRData,
    isValidating,
    mutate: mutateSWRCache,
  } = useSWR(dynamicSWRKey, fetchWithToken, {
    // In admin mode, show initial data while SWR fetches fresh data from server
    fallbackData: isAdminMode ? initialAdminApp : undefined,
    onError: (error) => {
      console.error(error);
      message.error("Something went wrong loading this application.");
      Sentry.captureException(error);
    },
  });

  const find = (flowId: string, applicationStatus: ApplicationStatus) => {
    // Admin mode: rawSWRData is a single OnboardingApplication
    if (isAdminMode) {
      const singleAdminApp = rawSWRData as OnboardingApplication;
      if (singleAdminApp && singleAdminApp.flowId === flowId) {
        // Ensure the application has all expected properties for compatibility
        return {
          ...singleAdminApp,
          status: singleAdminApp.status || applicationStatus,
          responses: singleAdminApp.responses || [],
        };
      }
    }

    // Regular mode: rawSWRData is { records: OnboardingApplication[] }
    const userApplicationsResponse = rawSWRData as {
      records: OnboardingApplication[];
    };
    if (!userApplicationsResponse) {
      return;
    }
    return userApplicationsResponse.records.find(
      (application) =>
        application.flowId === flowId &&
        application.status === applicationStatus
    );
  };

  const findResponse = (
    flowId: string,
    applicationStatus: ApplicationStatus,
    stepSlug: string
  ) => {
    // Try to find application through the find method (which handles admin mode)
    const application = find(flowId, applicationStatus);
    if (!application) {
      return;
    }

    // Ensure responses is an array
    const responses = application.responses || [];
    return responses.find((response) => response.slug === stepSlug);
  };

  // Find response for a specific application by providing the flow step's slug and id.
  const findResponseByApplicationId = (
    applicationId: string,
    stepSlug: string
  ) => {
    // Use the specific application provided.
    if (isAdminMode) {
      const adminApp = rawSWRData as OnboardingApplication;
      if (adminApp && adminApp.id === applicationId) {
        return adminApp.responses.find(
          (response) => response.slug === stepSlug
        );
      }
      return;
    }

    // Regular mode: search across all user applications
    const userApplicationsResponse = rawSWRData as {
      records: OnboardingApplication[];
    };
    if (!userApplicationsResponse) {
      return;
    }

    const application = userApplicationsResponse.records.find(
      (application) => application.id === applicationId
    );

    if (!application) {
      return;
    }

    return application.responses.find((response) => response.slug === stepSlug);
  };

  const findResponseField = (
    flowId: string,
    applicationStatus: ApplicationStatus,
    stepSlug: string | undefined,
    fieldKey: string | undefined
  ) => {
    if (!stepSlug || !fieldKey) {
      return;
    }
    const response = findResponse(flowId, applicationStatus, stepSlug);
    if (!response) {
      return;
    }
    return response.fields[fieldKey];
  };

  const create = async (
    flowId: string,
    response: ResponseStep,
    utmParams?: Record<string, any>,
    brandVariantId?: string,
    options?: { prefillRequestUuid?: string }
  ) => {
    try {
      await api.post(
        basePath,
        {
          flow: {
            id: flowId,
          },
          response,
          utmParams,
          brandVariantId,
          timeZone: browserTimeZone(),
          options,
        },
        {
          headers: {
            Authorization: tokenHelper.formatAuthHeader(
              tokenHelper.getJwtToken()
            ),
            // Hashed into meta.browserIdHash so the application joins the
            // browser's flow_pageviews rows.
            [BROWSER_ID_HEADER]: await browserId(),
          },
        }
      );
      // Invalidate SWR cache to trigger re-fetch and update UI
      await mutateSWRCache();
      return true;
    } catch (e) {
      // handle API error if user must be redirected to status portal
      if (
        axios.isAxiosError(e) &&
        e.response?.status === 303 &&
        e.response?.data?.redirectUrl
      ) {
        window.location.href = e.response.data.redirectUrl;
        return true;
      }
      //handle API error when only one submitted application is allowed
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        message.error(
          e.response?.data?.error ||
            "Only one submitted application is allowed. Please contact support"
        );
        router.replace(`/errors/${financialInstitutionId}/invalid`);
        return true;
      }
      console.error(e);
      message.error("Unable to create application");
      Sentry.captureException(e);
      return false;
    }
  };

  const update = async (
    applicationId: string,
    response: ResponseStep,
    applicationSubmitted = false
  ) => {
    const callApi = async () => {
      // In admin mode, use the JWT from localStorage directly
      let authHeader: string;
      if (isInAdminMode()) {
        // Find any JWT token in localStorage for admin mode
        const storageKeys = Object.keys(localStorage);
        let adminToken = null;
        for (const key of storageKeys) {
          if (key.includes("jwtToken")) {
            const token = localStorage.getItem(key);
            if (token && token.split(".").length === 3) {
              try {
                const payload = JSON.parse(atob(token.split(".")[1]));
                if (
                  payload.exp * 1000 > Date.now() &&
                  payload.user?.authenticationMode === "admin"
                ) {
                  adminToken = token;
                  break;
                }
              } catch (e) {
                // Invalid token, continue
              }
            }
          }
        }
        authHeader = adminToken
          ? `Bearer ${adminToken}`
          : tokenHelper.formatAuthHeader(tokenHelper.getJwtToken());
      } else {
        authHeader = tokenHelper.formatAuthHeader(tokenHelper.getJwtToken());
      }

      await api.patch(
        paths.applicationById(applicationId),
        {
          response,
          applicationSubmitted,
          // Omitted in admin mode: the zone describes where the applicant is,
          // and this call runs in an employee's browser when they edit an
          // application from the back office. The API discards it on the
          // admin-authenticated path, but the auth header above falls back to
          // the employee's ordinary token when no admin token is found in
          // localStorage, and that path does write it -- an employee in Denver
          // correcting a typo would otherwise relabel a Chicago applicant.
          //
          // `create` needs no equivalent: admin mode opens an application that
          // already exists, and that call has no admin branch at all.
          ...(isInAdminMode() ? {} : { timeZone: browserTimeZone() }),
        },
        {
          headers: {
            Authorization: authHeader,
          },
        }
      );
      // Invalidate SWR cache to trigger re-fetch and update UI
      await mutateSWRCache();
    };

    const handleError = (e: any) => {
      captureApiError(e);
      Sentry.captureMessage(
        `Save Failed: ${applicationId} || ${JSON.stringify(
          response
        )} || ${applicationSubmitted}`
      );
    };

    try {
      await callApiWithRetry(callApi, handleError);
      return true;
    } catch (e) {
      message.error("Unable to save application");
      return false;
    }
  };

  const close = async (application: OnboardingApplication) => {
    try {
      // TODO: create a separate endpoint for cancelled applications
      await api.patch(
        paths.applicationById(application.id),
        {
          response: application.responses[0],
          applicationCanceled: true,
        },
        {
          headers: {
            Authorization: tokenHelper.formatAuthHeader(
              tokenHelper.getJwtToken()
            ),
          },
        }
      );
      // Invalidate SWR cache to trigger re-fetch and update UI
      await mutateSWRCache();
      return true;
    } catch (e) {
      console.error(e);
      message.error("Unable to save application");
      Sentry.captureException(e);
      return false;
    }
  };

  // Generate a signing url for an embedded docusign document.
  const generateDocuSignSigningUrl = async ({
    applicationId,
    envelopeId,
    returnUrl,
    docuSignTemplateUuid,
  }: {
    applicationId: string;
    envelopeId?: string;
    returnUrl: string;
    docuSignTemplateUuid: string;
  }) => {
    return api.post(
      paths.signingUrlById(applicationId),
      { envelopeId, returnUrl, docuSignTemplateUuid },
      {
        headers: {
          Authorization: tokenHelper.formatAuthHeader(
            tokenHelper.getJwtToken()
          ),
        },
      }
    );
  };

  // Get an atomic web view url to instantiate an iframe with.
  const getAtomicSessionUrl = async ({
    applicationId,
    accounts,
    atomicConfigurationUuid,
  }: {
    applicationId: string;
    accounts: {
      accountNumber: string;
      routingNumber: string;
      accountType: string;
      title: string;
    }[];
    atomicConfigurationUuid: string;
  }) => {
    return api.post(
      paths.getAtomicSessionUrl(applicationId),
      { accounts, atomicConfigurationUuid },
      {
        headers: {
          Authorization: tokenHelper.formatAuthHeader(
            tokenHelper.getJwtToken()
          ),
        },
      }
    );
  };

  // Run rules to determine the next step in the flow.
  const getNextStep = async ({
    applicationId,
    stepSlug,
    transformUUID,
  }: {
    applicationId: string;
    stepSlug: string;
    transformUUID?: string;
  }) => {
    return api.post<{
      nextStep?: string;
    }>(
      paths.getNextStep(applicationId),
      { stepSlug, transformUUID },
      {
        headers: {
          Authorization: tokenHelper.formatAuthHeader(
            tokenHelper.getJwtToken()
          ),
        },
      }
    );
  };

  // Run Corelation Credit Pull for the application.
  const pullCorelationCredit = async (applicationId: string) => {
    return api.post<
      Array<{
        id: string;
        received: boolean;
      }>
    >(paths.pullCorelationCredit(applicationId), undefined, {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(tokenHelper.getJwtToken()),
      },
    });
  };

  // Run FIS GKYC evalaution for the application.
  const runFisGkycEvaluations = async (applicationId: string) => {
    return api.post(paths.runFisGkycEvaluations(applicationId), undefined, {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(tokenHelper.getJwtToken()),
      },
    });
  };

  const runSocureIdvSession = async (
    applicationId: string,
    options: { role?: string; delivery?: "inline" | "email" | "text" }
  ) => {
    return api.post<{
      sessionUuid: string;
      status: PersonIdSessionStatus;
      decision: PersonIdSessionDecision | null;
      captureUrl: string | null;
      qrCode: string | null;
      emailSent?: boolean;
      /**
       * Whether the request actually asked Socure to text the link. Not
       * "delivered" — a person with no mobile on file drops the flag
       * server-side, and the SMS itself is Socure's to deliver.
       */
      textRequested?: boolean;
    }>(paths.runSocureIdvSession(applicationId), options, {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(tokenHelper.getJwtToken()),
      },
    });
  };

  const getSocureIdvSession = async (
    applicationId: string,
    sessionUuid: string,
    /**
     * Ask the server to HOLD the response until the attempt settles, in seconds.
     *
     * This is what makes the QR hand-off responsive: the applicant finishes on
     * their phone while this window never loses focus, so there is no event to
     * react to and an interval would just wait out its own backoff. Omit it and
     * the endpoint answers immediately, exactly as it always did.
     */
    holdSeconds?: number,
    /**
     * Cancels the request. The caller uses this to retire a hold it has
     * superseded, so the server stops working on it rather than merely having
     * its answer ignored.
     */
    signal?: AbortSignal
  ) => {
    return api.get<{
      sessionUuid: string;
      status: PersonIdSessionStatus;
      decision: PersonIdSessionDecision | null;
      captureUrl: string | null;
      qrCode: string | null;
    }>(paths.getSocureIdvSession(applicationId, sessionUuid), {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(tokenHelper.getJwtToken()),
      },
      ...(signal ? { signal } : {}),
      ...(holdSeconds
        ? {
            params: { wait: holdSeconds },
            // Must outlast the hold, or axios aborts a request the server is
            // deliberately keeping open and every hold looks like a network
            // failure.
            //
            // Sized against the SERVER's budget, not the hold: the server caps
            // its whole response at `RESPONSE_BUDGET_MS` (27s) to stay inside
            // Heroku's 30s router, so anything past ~30s is the router's answer
            // and not something we should still be waiting on. An earlier 35s
            // sat above the router's own limit, which made it unreachable — it
            // could never have fired.
            timeout: 29_000,
          }
        : {}),
    });
  };

  // Run Experian queries for the application.
  const runExperianQueries = async (applicationId: string) => {
    return api.post(paths.runExperianQueries(applicationId), undefined, {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(tokenHelper.getJwtToken()),
      },
    });
  };

  const getFinancialApplicationCreditReport = async (id: string) => {
    return api.get<{
      id: string;
      received: boolean;
    }>(paths.getFinancialApplicationCreditReport(id), {
      headers: {
        Authorization: tokenHelper.formatAuthHeader(tokenHelper.getJwtToken()),
      },
    });
  };

  // Transform SWR data into consistent format for consuming components
  // Admin mode: wrap single app in array for compatibility with existing code
  // Regular mode: extract records array from response
  const normalizedApplicationData = isAdminMode
    ? rawSWRData
      ? [rawSWRData as OnboardingApplication]
      : [getAdminApplication()].filter(Boolean) // Fallback to initial data if SWR hasn't loaded yet
    : (rawSWRData as { records: OnboardingApplication[] })?.records;

  // Simple async function to fetch application by ID without SWR
  const getById = async (
    applicationId: string
  ): Promise<OnboardingApplication> => {
    try {
      if (!applicationId || tokenHelper.jwtIsExpired()) {
        throw new Error("Invalid application ID or expired token");
      }

      return fetchWithToken([
        paths.applicationById(applicationId),
        tokenHelper.getJwtToken(),
      ]);
    } catch (error: any) {
      console.error("Failed to load application:", error);
      if (error.response?.status === 403) {
        message.error("You do not have permission to access this application");
      } else {
        message.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to load application"
        );
      }
      Sentry.captureException(error);
      throw error;
    }
  };

  return {
    data: normalizedApplicationData,
    isValidating,
    find,
    findResponse,
    create,
    update,
    close,
    findResponseByApplicationId,
    findResponseField,
    generateDocuSignSigningUrl,
    getAtomicSessionUrl,
    getNextStep,
    getFinancialApplicationCreditReport,
    pullCorelationCredit,
    runFisGkycEvaluations,
    runSocureIdvSession,
    getSocureIdvSession,
    runExperianQueries,
    getById,
  };
};

// Custom hook for fetching a single application with SWR
// This follows React hooks rules by being a proper custom hook
export const useApplicationById = (
  applicationId: string | undefined,
  financialInstitutionId: string
) => {
  const tokenHelper = useAuthToken(financialInstitutionId);
  const { fetchWithToken } = useFetcher(api, financialInstitutionId);

  const shouldFetch =
    !!applicationId && !!financialInstitutionId && !tokenHelper.jwtIsExpired();

  return useSWR<OnboardingApplication>(
    shouldFetch
      ? [paths.applicationById(applicationId), tokenHelper.getJwtToken()]
      : null,
    fetchWithToken,
    {
      onError: (error) => {
        console.error("Failed to load application:", error);
        if (error.response?.status === 403) {
          message.error(
            "You do not have permission to access this application"
          );
        } else {
          message.error(
            error.response?.data?.message ||
              error.message ||
              "Failed to load application"
          );
        }
        Sentry.captureException(error);
      },
    }
  );
};

export default useApplication;
